// CargoMap.jsx — CLUSTER WHEN ZOOMED OUT (no spiderfy), SIDE-BY-SIDE WHEN ZOOMED IN
// Cluster click = small green halo + GREEN COUNT BADGE (icon unchanged)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  PolylineF,
} from "@react-google-maps/api";

import config from "../function/config";
import HomeNav from "./Navbars/HomeNav";
import AdminNav from "./Admin/AdminHome/AdminNav";
import Usernav from "./UserHome/Usernav";

import CargoIcon from "../images/CargoIcon.png";
import ShipIconImg from "../images/pin5.png";
import { toast } from "react-toastify";
import NewHomeNav from "./Navbars/NewHomeNav";
import NewAdminNav from "./Admin/AdminHome/NewAdminNav";

/* =========================== Google Maps (display only) =========================== */
const GMAPS_API_KEY = "AIzaSyDcu35S12sB5ryz4Ewd3hkZ745qASnKxMM";

const GMAPS_LIBRARIES = []; // IMPORTANT: same array used everywhere

export const GMAPS_LOADER_OPTIONS = {
  id: "gmaps-marine",           // <-- use this ID everywhere
  googleMapsApiKey: GMAPS_API_KEY,
  libraries: GMAPS_LIBRARIES,   // <-- same reference (not a new [])
  language: "en",
  region: "US",
};


/* =========================== DPR-aware icons =========================== */
const DPR = Math.min(window.devicePixelRatio || 1, 2);
function makeIconHD(url, displayW, displayH, anchorX, anchorY) {
  const w = Math.round(displayW);
  const h = Math.round(displayH);
  const natW = Math.round(displayW * DPR);
  const natH = Math.round(displayH * DPR);
  return {
    url,
    size: new window.google.maps.Size(natW, natH),
    scaledSize: new window.google.maps.Size(w, h),
    origin: new window.google.maps.Point(0, 0),
    anchor: new window.google.maps.Point(
      anchorX != null ? anchorX : Math.round(w / 2),
      anchorY != null ? anchorY : Math.round(h - 6)
    ),
  };
}

/* =========================== SVG pins / halos =========================== */
function svgThinPin({ fill = "#ef4444", stroke = "#0b1020", strokeWidth = 1.6, size = 26 }) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 64">
      <path d="M26 62 s20-20.7 20-34 c0-11.05-8.95-20-20-20 s-20 8.95-20 20 c0 13.3 20 34 20 34z"
            fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" />
      <circle cx="26" cy="26" r="6" fill="#ffffff" stroke="${stroke}" stroke-width="${strokeWidth * 0.9}"/>
    </svg>`;
  const h = Math.round(size * (64 / 52));
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(size, h),
    anchor: new window.google.maps.Point(Math.round(size / 2), Math.round(h - 6)),
  };
}

// small green highlight halo
function haloIcon({
  diameter = 84,
  stroke = "rgba(34,197,94,0.70)",
  fill = "rgba(34,197,94,0.18)",
  strokeWidth = 4,
} = {}) {
  const r = Math.round(diameter / 2);
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${diameter}" height="${diameter}">
  <circle cx="${r}" cy="${r}" r="${r - strokeWidth / 2}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>
</svg>`.trim();
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  return {
    url,
    size: new window.google.maps.Size(diameter, diameter),
    scaledSize: new window.google.maps.Size(diameter, diameter),
    anchor: new window.google.maps.Point(r, r),
  };
}

/* =========================== Badges =========================== */
function makeRefBadgeIcon(
  text,
  {
    fontSize = 12,
    hPad = 8,
    vPad = 4,
    radius = 999,
    bg = "#EF4444", // can be overridden to green when cluster is selected
    fg = "#FFFFFF",
    yOffset = 52,
  } = {}
) {
  const safe = String(text || "");
  const estChar = Math.max(1, safe.length);
  const charW = Math.round(fontSize * 0.62);
  const textW = estChar * charW;
  const w = textW + hPad * 2;
  const h = fontSize + vPad * 2;

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
  <rect x="0" y="0" rx="${radius}" ry="${radius}" width="${w}" height="${h}" fill="${bg}" opacity="0.96"/>
  <text x="${w / 2}" y="${Math.round(h / 2 + fontSize / 2 - 2)}"
        font-family="Inter,system-ui,Segoe UI,Roboto,Helvetica,Arial"
        font-size="${fontSize}" font-weight="700" text-anchor="middle" fill="${fg}">${safe
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")}</text>
</svg>`.trim();

  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  if (window.google?.maps) {
    return {
      url,
      size: new window.google.maps.Size(w, h),
      scaledSize: new window.google.maps.Size(w, h),
      anchor: new window.google.maps.Point(Math.round(w / 2), h + yOffset),
    };
  }
  return { url };
}

/* =========================== Utils =========================== */
const safeParseJSON = (s, f) => {
  if (s === null || s === undefined || s === "") return f;
  try {
    const v = JSON.parse(s);
    return v === null ? f : v;
  } catch {
    return f;
  }
};
const norm = (s = "") =>
  s.trim().toLowerCase().replace(/\s+/g, " ").replace(/,+/g, ",");
const isVisible = (v) =>
  !(v === false || v === 0 || v === "0" || v === "false" || v === "False");
const pretty = (s = "") => s.replace(/\b\w/g, (m) => m.toUpperCase());
function timeAgo(dateish) {
  if (!dateish) return "";
  const t = new Date(dateish).getTime();
  if (!Number.isFinite(t)) return "";
  const sec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  const units = [
    ["y", 31536000],
    ["mo", 2592000],
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
    ["s", 1],
  ];
  for (const [u, s] of units) {
    if (sec >= s) {
      const v = Math.floor(sec / s);
      return `${v}${u} ago`;
    }
  }
  return "";
}

/* =========================== Geo helpers =========================== */
function toRad(d) {
  return (d * Math.PI) / 180;
}
function toDeg(r) {
  return (r * 180) / Math.PI;
}
function moveByBearing(lat, lon, distanceMeters, bearingDeg) {
  const R = 6371000,
    φ1 = (lat * Math.PI) / 180,
    λ1 = (lon * Math.PI) / 180,
    θ = (bearingDeg * Math.PI) / 180,
    δ = distanceMeters / R;
  const sinφ1 = Math.sin(φ1),
    cosφ1 = Math.cos(φ1),
    sinδ = Math.sin(δ),
    cosδ = Math.cos(δ);
  const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * Math.cos(θ);
  const φ2 = Math.asin(sinφ2);
  const y = Math.sin(θ) * sinδ * cosφ1;
  const x = cosδ - sinφ1 * sinφ2;
  const λ2 = λ1 + Math.atan2(y, x);
  return [
    (φ2 * 180) / Math.PI,
    (((λ2 * 180) / Math.PI + 540) % 360) - 180,
  ];
}
function greatCircleRoute(start, end, segments = 160) {
  const [lat1, lon1] = [toRad(start[0]), toRad(start[1])];
  const [lat2, lon2] = [toRad(end[0]), toRad(end[1])];
  const d =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((lat2 - lat1) / 2) ** 2 +
          Math.cos(lat1) *
            Math.cos(lat2) *
            Math.sin((lon2 - lon1) / 2) ** 2
      )
    );
  if (!d) return [start, end];
  const out = [];
  for (let i = 0; i <= segments; i++) {
    const f = i / segments;
    const A = Math.sin((1 - f) * d) / Math.sin(d);
    const B = Math.sin(f * d) / Math.sin(d);
    const x =
      A * Math.cos(lat1) * Math.cos(lon1) +
      B * Math.cos(lat2) * Math.cos(lon2);
    const y =
      A * Math.cos(lat1) * Math.sin(lon1) +
      B * Math.cos(lat2) * Math.sin(lon2);
    const z = A * Math.sin(lat1) + B * Math.sin(lat2);
    const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
    const lon = Math.atan2(y, x);
    out.push([toDeg(lat), ((toDeg(lon) + 540) % 360) - 180]);
  }
  return out;
}

/* =========================== Bearings / offsets =========================== */
const seaBearingOverrides = {
  dubai: 305,
  mumbai: 255,
  "mundra port": 240,
  jingtang: 100,
  kandla: 240,
  shanghai: 110,
  arzew: 340,
  "port of djen djen": 0,
  "port of djen djen, algeria": 0,
  "djen djen": 0,
};
const seaDistanceOverridesKm = {
  dubai: 18,
  kandla: 25,
  "mundra port": 25,
  jingtang: 40,
  shanghai: 35,
  arzew: 18,
};
function pickSeaBearing(areaName, lon) {
  const k = norm(String(areaName || ""));
  const first = k.split(",")[0];
  if (seaBearingOverrides[first]) return seaBearingOverrides[first];
  if (seaBearingOverrides[k]) return seaBearingOverrides[k];
  for (const key in seaBearingOverrides)
    if (first.includes(key) || k.includes(key)) return seaBearingOverrides[key];
  return lon >= 0 ? 135 : 225;
}
function pickSeaDistanceKm(areaName, fallbackKm = 60) {
  const k = norm(String(areaName || ""));
  const first = k.split(",")[0];
  if (seaDistanceOverridesKm[first] != null)
    return seaDistanceOverridesKm[first];
  if (seaDistanceOverridesKm[k] != null) return seaDistanceOverridesKm[k];
  for (const key in seaDistanceOverridesKm)
    if (first.includes(key) || k.includes(key))
      return seaDistanceOverridesKm[key];
  return fallbackKm;
}
function inlandBearing(areaName, lon) {
  return (pickSeaBearing(areaName, lon) + 180) % 360;
}
function inlandAnchor(baseLat, baseLon, areaName, km = 0.35) {
  const b = inlandBearing(areaName, baseLon);
  return moveByBearing(baseLat, baseLon, km * 1000, b);
}
function inlandSideBySidePositions(
  baseLat,
  baseLon,
  areaName,
  count,
  spacingMeters = 120,
  km = 0.35
) {
  const b = inlandBearing(areaName, baseLon);
  const anchor = moveByBearing(baseLat, baseLon, km * 1000, b);
  const lateral = (b + 90) % 360;
  const half = (count - 1) / 2;
  return Array.from({ length: count }, (_, i) => {
    const delta = (i - half) * spacingMeters;
    const dir = delta >= 0 ? lateral : (lateral + 180) % 360;
    return moveByBearing(anchor[0], anchor[1], Math.abs(delta), dir);
  });
}

/* =========================== Aliases / Overrides =========================== */
const INITIAL_CENTER = [20, 0];
const USER_POS_KEY = "mariniz:lastUserPos";
const DID_FLY_KEY = "mariniz:didFlyThisPage";

const AREA_ALIASES = {
  bombay: "mumbai",
  "bombay, india": "mumbai, india",
  mundra: "mundra port",
  "mundra, india": "mundra port",
  "mundra port, india": "mundra port",
  "jingtang port": "jingtang",
  "jingtang, china": "jingtang",
  "tangshan jingtang": "jingtang",
  "zhuhai gaolan": "zhuhai gaolan port",
  gaolan: "zhuhai gaolan port",
  "gaolan, zhuhai": "zhuhai gaolan port",
  "gaolan port": "zhuhai gaolan port",
};
const AREA_COORD_OVERRIDES = {
  mumbai: [19.076, 72.8777],
  "mumbai, india": [19.076, 72.8777],
  kandla: [23.033, 70.216],
  "mundra port": [22.7359, 69.7036],
  jingtang: [39.214, 119.012],
  "zhuhai gaolan port": [21.952231, 113.205567],
  gaolan: [21.952231, 113.205567],
};
const PORT_COORD_OVERRIDES = {
  kandla: [23.033, 70.216],
  "deendayal port": [23.033, 70.216],
  "deendayal port, kandla": [23.033, 70.216],
  "kandla, gujarat": [23.033, 70.216],
  "zhuhai gaolan port": [21.952231, 113.205567],
  "port sudan": [19.6175, 37.216],
  "port sudan, sudan": [19.6175, 37.216],
  "port of djen djen, algeria": [36.82215, 5.88551],
  "port of djen djen": [36.82215, 5.88551],
  "djen djen": [36.82215, 5.88551],
  "djen-djen": [36.82215, 5.88551],
  "djen djen, algeria": [36.82215, 5.88551],
};
const PORT_ALIASES = {
  "deendayal port authority": "deendayal port",
  "deendayal port kandla": "deendayal port, kandla",
  "kandla port": "kandla",
  "kandla port, india": "kandla",
  "kandla, india": "kandla",
  "kandla, gujarat": "kandla",
  "gandhidham (kandla)": "kandla",
  "deendayal port (kandla)": "deendayal port",
  "zhuhai gaolan": "zhuhai gaolan port",
  gaolan: "zhuhai gaolan port",
  "gaolan port": "zhuhai gaolan port",
  "gaolan, zhuhai": "zhuhai gaolan port",
};
const ALWAYS_VISIBLE_PORTS = [
  { name: "Kandla", lat: 23.033, lon: 70.216 },
  { name: "Mundra Port", lat: 22.7359, lon: 69.7036 },
  { name: "Hazira Port", lat: 21.1168, lon: 72.6385 },
  { name: "Zhuhai Gaolan Port", lat: 21.952231, lon: 113.205567 },
];

/* NEW: ship-specific coastal anchors so ships never sit inland */
const SHIP_AREA_OVERRIDES = {
  dubai: [25.26, 55.285],
  "dubai, uae": [25.26, 55.285],
  "jebel ali": [25.01, 55.04],
  "jebel ali, uae": [25.01, 55.04],
  "abu dhabi": [24.48, 54.37],
  "abu dhabi, uae": [24.48, 54.37],
  kandla: [23.01, 70.18],
  "mundra port": [22.73, 69.71],
  hazira: [21.1168, 72.6385],
  mumbai: [18.95, 72.84],
  "mumbai, india": [18.95, 72.84],
  shanghai: [31.36, 121.73],
  "shanghai, china": [31.36, 121.73],
  jingtang: [39.214, 119.012],
  "zhuhai gaolan port": [21.952231, 113.205567],
};

const GEO_CACHE_KEY = "portGeoCache_v1";

/* =========================== Cache helpers =========================== */
function normAreaKey(s) {
  const k = norm(String(s || ""));
  return AREA_ALIASES[k] || k;
}
function applyAreaOverrideIfAny(k, fallback) {
  const ov = AREA_COORD_OVERRIDES[k];
  if (ov) return [Number(ov[0]), Number(ov[1])];
  return fallback;
}
function readLastUserPos() {
  try {
    const raw =
      sessionStorage.getItem(USER_POS_KEY) ||
      localStorage.getItem(USER_POS_KEY);
    if (!raw) return null;
    const { lat, lon } = JSON.parse(raw);
    if (typeof lat === "number" && typeof lon === "number") return [lat, lon];
  } catch {}
  return null;
}
function writeLastUserPos(pos) {
  try {
    const [lat, lon] = pos;
    const payload = JSON.stringify({ lat, lon, t: Date.now() });
    sessionStorage.setItem(USER_POS_KEY, payload);
    localStorage.setItem(USER_POS_KEY, payload);
  } catch {}
}
const readGeoCache = () => {
  try {
    const raw = safeParseJSON(localStorage.getItem(GEO_CACHE_KEY), {});
    const now = Date.now();
    const cleaned = {};

    for (const k in raw) {
      const v = raw[k];
      if (!v) continue;
      if (!v.t || now - v.t < 7 * 24 * 60 * 60 * 1000) cleaned[k] = v;
    }

    if (Object.keys(cleaned).length !== Object.keys(raw).length) {
      localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cleaned));
    }
    return cleaned;
  } catch {
    return {};
  }
};

const writeGeoCache = (obj) => {
  try {
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(obj));
  } catch {}
};

/* =========================== Port helpers =========================== */
const getCargoKey = (c) =>
  `${c?.id || c?._id || c?.reference_id || "noid"}::${
    c?.loading_port ? norm(String(c.loading_port)) : ""
  }::${c?.discharge_port ? norm(String(c.discharge_port)) : ""}`;
const getShipKey = (s) =>
  `${s?.id || s?._id || s?.reference_id || "noid"}::${norm(
    String(s?.open_area || "")
  )}`;
const getId = (c) => c?.id ?? c?._id ?? c?.reference_id;

function getLoadKey(c) {
  const raw = norm(String(c?.loading_port || ""));
  return PORT_ALIASES[raw] || raw;
}
function getCargoLoadPos(c, coordsMap) {
  const key = getLoadKey(c);
  return coordsMap[key] || fallbackGuessCoords(key) || null;
}
function fallbackGuessCoords(portKey) {
  if (PORT_COORD_OVERRIDES[portKey]) return PORT_COORD_OVERRIDES[portKey];
  if (AREA_COORD_OVERRIDES[portKey]) return AREA_COORD_OVERRIDES[portKey];
  const needle = (portKey || "").toLowerCase();
  const found = ALWAYS_VISIBLE_PORTS.find(
    (p) =>
      p.name.toLowerCase() === needle ||
      needle.includes(p.name.toLowerCase()) ||
      p.name.toLowerCase().includes(needle)
  );
  if (found) return [found.lat, found.lon];
  return null;
}

/* ======================================================================= */
/* ============================== MAIN COMPONENT ========================= */
/* ======================================================================= */
export default function CargoMap() {
  const [cargos, setCargos] = useState([]);
  const [portCoords, setPortCoords] = useState(() => {
    const cache = safeParseJSON(localStorage.getItem(GEO_CACHE_KEY), {});
    const map = {};
    for (const k in cache) {
      const v = cache[k];
      if (v?.lat != null && v?.lon != null)
        map[k] = [Number(v.lat), Number(v.lon)];
    }
    return map;
  });
  const [ships, setShips] = useState([]);
  const [areaCoords, setAreaCoords] = useState({});
  const [showShips, setShowShips] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [center, setCenter] = useState({
    lat: INITIAL_CENTER[0],
    lng: INITIAL_CENTER[1],
  });
  const [zoom, setZoom] = useState(5);

  const [route, setRoute] = useState(null);
  const [dest, setDest] = useState(null);
  const [activeBase, setActiveBase] = useState("Light");
  const [user, setUser] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [gmapsReady, setGmapsReady] = useState(false);
  const [mapZoom, setMapZoom] = useState(5);
  const [showSidebar, setShowSidebar] = useState(true);
  const [sidebarSearch, setSidebarSearch] = useState("");

  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileListRef = useRef(null);

  const [hoveredCargoKey, setHoveredCargoKey] = useState(null);
  const [hoveredShipKey, setHoveredShipKey] = useState(null);

  const selectedCargoKeyRef = useRef(null);
  const routeCacheRef = useRef(new Map());
  const mapRef = useRef(null);
  const latestCargoIdsRef = useRef(new Set());
  const hideNotificationTimeoutRef = useRef(null);

  const fitDepsRef = useRef({ routeKey: null, showSidebar });

  const [selectedCargoKey, setSelectedCargoKey] = useState(null);
  const [selectedShipKey, setSelectedShipKey] = useState(null);
  const [newCargoNotification, setNewCargoNotification] = useState(null);

  useEffect(() => {
    selectedCargoKeyRef.current = selectedCargoKey;
  }, [selectedCargoKey]);

  const token = useMemo(
    () => localStorage.getItem("token") || sessionStorage.getItem("token"),
    []
  );
  const [navVariant, setNavVariant] = useState(() => {
    const cachedAdmin = localStorage.getItem("is_superuser") === "1";
    if (cachedAdmin) return "admin";
    if (token) return "user";
    return "guest";
  });

  // Edit/Delete
  const [editCargo, setEditCargo] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  function handleEditClick(c) {
    setEditCargo(c);
    setEditForm({
      type_of_cargo: c?.type_of_cargo ?? "",
      loading_port: c?.loading_port ?? "",
      discharge_port: c?.discharge_port ?? "",
      tonnage: c?.tonnage ?? "",
      lychan: c?.lychan ?? "",
      commission: c?.commission ?? "",
      charterer_account: c?.charterer_account ?? "",
      brief_description: c?.brief_description ?? "",
      name: c?.name ?? "",
      email: c?.email ?? "",
      phone: c?.phone ?? "",
      company: c?.company ?? "",
      is_status: c?.is_status ?? true,
      url_new: c?.url_new ?? "",
    });
  }
  async function saveEdit() {
    if (!editCargo) return;
    const id = getId(editCargo);
    if (!id) {
      alert("Missing cargo id");
      return;
    }
    try {
      setIsSavingEdit(true);
      await axios.put(
        `${config.base_url}/api/HappyMarineShipping/updateLogistics/${id}`,
        editForm,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(token ? { Authorization: `Token ${token}` } : {}),
          },
        }
      );
      setCargos((prev) =>
        prev.map((x) => (getId(x) === id ? { ...x, ...editForm } : x))
      );
      setEditCargo(null);
      toast.success("Cargo updated");
    } catch (e) {
      console.error(e);
      alert("Update failed");
    } finally {
      setIsSavingEdit(false);
    }
  }
  function handleDeleteClick(c) {
    setConfirmDel(c);
  }
  async function confirmDeleteNow() {
    if (!confirmDel) return;
    const id = getId(confirmDel);
    if (!id) {
      alert("Missing cargo id");
      return;
    }
    try {
      setIsDeleting(true);
      await axios.delete(
        `${config.base_url}/api/HappyMarineShipping/deleteLogistic/${id}`,
        {
          headers: { ...(token ? { Authorization: `Token ${token}` } : {}) },
        }
      );
      setCargos((prev) => prev.filter((x) => getId(x) !== id));
      const delKey = getCargoKey(confirmDel);
      if (selectedCargoKey === delKey) {
        setSelectedCargoKey(null);
        setRoute(null);
        setDest(null);
      }
      setConfirmDel(null);
    } catch (e) {
      console.error(e);
      alert("Delete failed");
    } finally {
      setIsDeleting(false);
    }
  }

  // Optional stub to avoid undefined reference from modal
  function handleRepost() {
    toast.info("Repost action will be implemented soon.");
  }

  const [ownerOpenKey, setOwnerOpenKey] = useState(null);

  // Sidebar state
  const [sidebarMode, setSidebarMode] = useState("recent"); // 'recent' | 'nearby' | 'cluster'
  const [nearbyAnchor, setNearbyAnchor] = useState(null);
  const cardRefs = useRef({});
  const sidebarListRef = useRef(null);

  // Selected cluster (for highlight + cluster sidebar)
  const [selectedClusterLoadKey, setSelectedClusterLoadKey] = useState(null);

  // ---------- Robust scroll helper ----------
  function scrollCardIntoView(cargoKey) {
    if (!cargoKey) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = cardRefs.current[cargoKey];
        const container = sidebarListRef.current;
        if (el && container) {
          const top = el.offsetTop - container.offsetTop - 8;
          container.scrollTo({ top, behavior: "smooth" });
        }
      });
    });
  }

  // Scroll selected cargo card to TOP when a cargo is selected
  useEffect(() => {
    if (!selectedCargoKey) return;
    scrollCardIntoView(selectedCargoKey);
  }, [selectedCargoKey]);

  // Keep `nearbyAnchor` updated when selecting cargo,
  // but DON'T override "cluster" mode.
  useEffect(() => {
    if (!selectedCargoKey) return;
    const sel = cargos.find((x) => getCargoKey(x) === selectedCargoKey);
    const pos = sel ? getCargoLoadPos(sel, portCoords) : null;
    if (!pos) return;

    // Only auto-switch from "recent" -> "nearby".
    if (sidebarMode === "recent" && !selectedClusterLoadKey) {
      setSidebarMode("nearby");
    }

    setNearbyAnchor((prev) => prev ?? { key: getCargoKey(sel), pos });
  }, [selectedCargoKey, cargos, portCoords, sidebarMode, selectedClusterLoadKey]);

  const isLoggedIn = !!(token || user);
  useEffect(() => {
    if (!isLoggedIn) setOwnerOpenKey(null);
  }, [isLoggedIn]);

  /* ========== Google Loader ========== */
 /* ========== Google Loader ========== */
const { isLoaded } = useJsApiLoader(GMAPS_LOADER_OPTIONS);


  /* ========== Icons ========== */
  const cargoIcon = useMemo(
    () =>
      window.google?.maps
        ? makeIconHD(CargoIcon, 52, 52, 26, 46)
        : { url: CargoIcon },
    [isLoaded]
  );

  const selectedCargoIcon = useMemo(
    () =>
      window.google?.maps
        ? makeIconHD(CargoIcon, 58, 58, 29, 50)
        : { url: CargoIcon },
    [isLoaded]
  );

  const shipIcon = useMemo(
    () =>
      window.google?.maps
        ? makeIconHD(ShipIconImg, 52, 52, 26, 46)
        : { url: ShipIconImg },
    [isLoaded]
  );

  const destIcon = useMemo(
    () =>
      window.google?.maps
        ? svgThinPin({ size: 36, fill: "#ef4444", stroke: "#0b1020", strokeWidth: 1.4 })
        : undefined,
    [isLoaded]
  );

  useEffect(() => {
    [CargoIcon, ShipIconImg].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  /* ======== FREE PHOTON / OSM GEOCODING (NO BACKEND) ========= */

  // Clean port/area names a bit before sending to geocoder
  function cleanPortQuery(q) {
    return String(q || "")
      .replace(/\bport\b/gi, "")
      .replace(/\bterminal\b/gi, "")
      .replace(/\bsea\s*port\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  /**
   * Geocode a list of queries using the free Photon / OSM service.
   * No backend /api/geocode/batch is used anymore.
   *
   * Returns: [{ q, lat, lon }, ...]
   */
  async function geocodeBatch(queries) {
    if (!queries?.length) return [];

    const out = [];

    for (const rawQ of queries) {
      const qNorm = norm(String(rawQ || ""));
      if (!qNorm) continue;

      const cleaned = cleanPortQuery(rawQ);
      const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(
        cleaned
      )}&limit=1`;

      try {
        const res = await axios.get(url, {
          timeout: 10000,
          headers: {
            Accept: "application/json",
          },
        });

        const feature = res?.data?.features?.[0];
        const coords = feature?.geometry?.coordinates;
        const lon = Array.isArray(coords) ? coords[0] : null;
        const lat = Array.isArray(coords) ? coords[1] : null;

        out.push({
          q: qNorm,
          lat: typeof lat === "number" ? lat : null,
          lon: typeof lon === "number" ? lon : null,
        });
      } catch (err) {
        console.error("Photon geocode error for", rawQ, err);
        out.push({ q: qNorm, lat: null, lon: null });
      }

      // Small delay to be nice to the free service
      await new Promise((r) => setTimeout(r, 120));
    }

    return out;
  }

  async function resolveAreasBatch(areaKeys) {
    const cacheRaw = readGeoCache();
    const outMap = {};
    const toFetch = [];

    for (const k of areaKeys) {
      const ov = applyAreaOverrideIfAny(k, undefined);
      if (ov) {
        outMap[k] = ov;
        continue;
      }

      const hit = cacheRaw[k];
      if (hit?.lat != null && hit?.lon != null) {
        outMap[k] = [hit.lat, hit.lon];
      } else {
        toFetch.push(k);
      }
    }

    const BATCH = 50;
    for (let i = 0; i < toFetch.length; i += BATCH) {
      const slice = toFetch.slice(i, i + BATCH);
      const res = await geocodeBatch(slice);
      res.forEach(({ q, lat, lon }) => {
        if (lat != null && lon != null) {
          outMap[q] = [lat, lon];
          cacheRaw[q] = { lat, lon, t: Date.now() };
        }
      });
    }
    writeGeoCache(cacheRaw);
    return outMap;
  }

  async function resolvePortsBatch(portKeys) {
    const cacheRaw = readGeoCache();
    const outMap = {};
    const toFetch = [];

    for (const k of portKeys) {
      if (PORT_COORD_OVERRIDES[k]) {
        outMap[k] = PORT_COORD_OVERRIDES[k];
        continue;
      }
      if (AREA_COORD_OVERRIDES[k]) {
        outMap[k] = AREA_COORD_OVERRIDES[k];
        continue;
      }

      const hit = cacheRaw[k];
      if (hit?.lat != null && hit?.lon != null) {
        outMap[k] = [hit.lat, hit.lon];
      } else {
        toFetch.push(k);
      }
    }

    const BATCH = 50;
    for (let i = 0; i < toFetch.length; i += BATCH) {
      const slice = toFetch.slice(i, i + BATCH);
      const res = await geocodeBatch(slice);
      res.forEach(({ q, lat, lon }) => {
        if (lat != null && lon != null) {
          outMap[q] = [lat, lon];
          cacheRaw[q] = { lat, lon, t: Date.now() };
        }
      });
    }
    writeGeoCache(cacheRaw);
    return outMap;
  }

  useEffect(() => {
    const cached = readLastUserPos();
    if (cached && !userPos) setUserPos(cached);
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const fast = [pos.coords.latitude, pos.coords.longitude];
        writeLastUserPos(fast);
        setUserPos((prev) => prev || fast);
      },
      () => {},
      {
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: 6 * 60 * 60 * 1000,
      }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) {
      setNavVariant("guest");
      return;
    }
    const controller = new AbortController();
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get(
          `${config.base_url}/api/HappyMarineShipping/viewusers`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Token ${token}`,
            },
            signal: controller.signal,
          }
        );
        if (ignore) return;
        if (res?.data?.status === 200) {
          const u = res.data.data;
          setUser(u);
          const isAdmin = !!u?.is_superuser;
          setNavVariant(isAdmin ? "admin" : "user");
          localStorage.setItem("is_superuser", isAdmin ? "1" : "0");
        } else {
          setNavVariant("guest");
          localStorage.removeItem("is_superuser");
        }
      } catch {
        if (!ignore) {
          setNavVariant("guest");
          localStorage.removeItem("is_superuser");
        }
      }
    })();
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [token]);

  /* ========== Fetch cargos with polling + "new cargo approved" notification ========== */
  useEffect(() => {
    let ignore = false;

    const fetchCargos = async (isInitial = false) => {
      try {
        const r = await axios.get(
          `${config.base_url}/api/HappyMarineShipping/viewLogistics`,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        if (ignore) return;

        if (r.data?.status === 200) {
          const visibleRows = (r.data.data || []).filter((c) =>
            isVisible(c?.is_status)
          );

          setCargos(visibleRows);

          // Build current ID set
          const currentIds = new Set(visibleRows.map((c) => getId(c)));

          if (isInitial) {
            // On first load, just remember what exists; no notification.
            latestCargoIdsRef.current = currentIds;
            return;
          }

          const prevIds = latestCargoIdsRef.current || new Set();
          let newestCargo = null;

          // Look for cargos that were not present before (newly approved/added)
          for (const c of visibleRows) {
            const id = getId(c);
            if (!prevIds.has(id)) {
              if (!newestCargo) {
                newestCargo = c;
              } else {
                const tsNew = new Date(
                  c?.created_at ||
                    c?.createdAt ||
                    c?.updated_at ||
                    0
                ).getTime();
                const tsBest = new Date(
                  newestCargo?.created_at ||
                    newestCargo?.createdAt ||
                    newestCargo?.updated_at ||
                    0
                ).getTime();
                if (tsNew > tsBest) newestCargo = c;
              }
            }
          }

          // Save current IDs
          latestCargoIdsRef.current = currentIds;

          // If we found a newly-approved cargo, show a 5 second notification
          if (newestCargo) {
            setNewCargoNotification({ cargo: newestCargo });

            if (hideNotificationTimeoutRef.current) {
              clearTimeout(hideNotificationTimeoutRef.current);
            }
            hideNotificationTimeoutRef.current = setTimeout(() => {
              setNewCargoNotification((prev) =>
                prev &&
                getCargoKey(prev.cargo) === getCargoKey(newestCargo)
                  ? null
                  : prev
              );
            }, 5000);
          }
        }
      } catch (e) {
        console.error("fetch cargos failed", e);
      }
    };

    // Initial load
    fetchCargos(true);
    // Poll every 30 seconds to detect newly-approved cargos
    const intervalId = setInterval(() => fetchCargos(false), 30000);

    return () => {
      ignore = true;
      clearInterval(intervalId);
      if (hideNotificationTimeoutRef.current) {
        clearTimeout(hideNotificationTimeoutRef.current);
      }
    };
  }, []);

  /* ========== Fetch ships (unchanged logic, but new sea placement below) ========== */
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(
          `${config.base_url}/api/HappyMarineShipping/viewShipForCharter`,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        if (r.data?.status === 200) {
          const rows = (r.data.data || []).filter((s) =>
            isVisible(s?.is_status)
          );
          setShips(rows);
        }
      } catch (e) {
        console.error("fetch ships failed", e);
      }
    })();
  }, []);

  /* ========== Resolve coords (with Photon batch) ========== */
  // FAST LOAD: incremental geocoding so cargos appear on map quickly
  useEffect(() => {
    if (!cargos.length) return;
    let ignore = false;

    (async () => {
      // collect all ports (load + discharge)
      const all = [];
      cargos.forEach((c) => {
        if (c?.loading_port) all.push(norm(String(c.loading_port)));
        if (c?.discharge_port) all.push(norm(String(c.discharge_port)));
      });
      const unique = Array.from(new Set(all))
        .filter(Boolean)
        .map((p) => PORT_ALIASES[p] || p);
      if (!unique.length) return;

      const cacheRaw = readGeoCache();
      const now = Date.now();
      const initialUpdates = {};

      // 1) apply overrides + cached coords immediately
      unique.forEach((k) => {
        if (PORT_COORD_OVERRIDES[k]) {
          initialUpdates[k] = PORT_COORD_OVERRIDES[k];
          return;
        }
        if (AREA_COORD_OVERRIDES[k]) {
          initialUpdates[k] = AREA_COORD_OVERRIDES[k];
          return;
        }
        const hit = cacheRaw[k];
        if (hit?.lat != null && hit?.lon != null) {
          initialUpdates[k] = [Number(hit.lat), Number(hit.lon)];
        }
      });

      if (Object.keys(initialUpdates).length && !ignore) {
        setPortCoords((prev) => ({ ...prev, ...initialUpdates }));
      }

      // 2) find remaining ports that still need geocoding
      const toFetch = unique.filter(
        (k) => !initialUpdates[k] && !portCoords[k]
      );
      if (!toFetch.length || ignore) return;

      const BATCH = 10; // smaller batch => first markers show faster

      for (let i = 0; i < toFetch.length && !ignore; i += BATCH) {
        const slice = toFetch.slice(i, i + BATCH);
        const res = await geocodeBatch(slice);
        const batchUpdates = {};
        res.forEach(({ q, lat, lon }) => {
          if (lat != null && lon != null) {
            batchUpdates[q] = [lat, lon];
            cacheRaw[q] = { lat, lon, t: now };
          }
        });

        writeGeoCache(cacheRaw);

        if (Object.keys(batchUpdates).length && !ignore) {
          setPortCoords((prev) => ({ ...prev, ...batchUpdates }));
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [cargos]); // eslint-disable-line react-hooks/exhaustive-deps

  // FAST LOAD: start geocoding ship open areas as soon as ships are fetched
  useEffect(() => {
    if (ships.length === 0) return;
    (async () => {
      const uniqueAreas = Array.from(
        new Set(
          ships
            .filter((s) => isVisible(s?.is_status))
            .map((s) => normAreaKey(s?.open_area || ""))
            .filter(Boolean)
        )
      );

      const next = { ...areaCoords };
      uniqueAreas.forEach((k) => {
        if (!next[k]) {
          const ov = applyAreaOverrideIfAny(k, undefined);
          if (ov) next[k] = ov;
        }
      });

      const missing = uniqueAreas.filter((k) => !next[k]);
      if (missing.length) {
        const resolved = await resolveAreasBatch(missing);
        for (const k of Object.keys(resolved)) next[k] = resolved[k];
      }
      setAreaCoords((prev) => ({ ...prev, ...next }));
    })();
  }, [ships]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ========== URL deep link for cargo (READ ONLY, no replaceState) ========== */
  useEffect(() => {
    let deep = null;
    try {
      deep = new URL(window.location.href).searchParams.get("cargo");
    } catch {}
    if (deep) setSelectedCargoKey(deep);
  }, []);

  /* ========== Filters (chips) ========== */
  const [loadSearch, setLoadSearch] = useState("");
  const [discSearch, setDiscSearch] = useState("");
  const [selectedLoadPorts, setSelectedLoadPorts] = useState([]);
  const [selectedDiscPorts, setSelectedDiscPorts] = useState([]);
  const toggleLoadChip = (key) =>
    setSelectedLoadPorts((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  const toggleDiscChip = (key) =>
    setSelectedDiscPorts((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  const clearFilters = () => {
    setSelectedLoadPorts([]);
    setSelectedDiscPorts([]);
    setLoadSearch("");
    setDiscSearch("");
  };

  const { loadPortOptions, discPortOptions, topLoadChips, topDiscChips } =
    useMemo(() => {
      const loadCounts = {},
        discCounts = {};
      cargos
        .filter((c) => isVisible(c?.is_status))
        .forEach((c) => {
          const ld =
            PORT_ALIASES[norm(String(c.loading_port || ""))] || "";
          const dd =
            PORT_ALIASES[norm(String(c.discharge_port || ""))] || "";
          if (ld) loadCounts[ld] = (loadCounts[ld] || 0) + 1;
          if (dd) discCounts[dd] = (discCounts[dd] || 0) + 1;
        });
      const toList = (obj) =>
        Object.entries(obj)
          .sort((a, b) => b[1] - a[1])
          .map(([key, count]) => ({
            key,
            count,
            label: key.replace(/\b\w/g, (m) => m.toUpperCase()),
          }));
      const loadList = toList(loadCounts);
      const discList = toList(discCounts);
      return {
        loadPortOptions: loadList,
        discPortOptions: discList,
        topLoadChips: loadList.slice(0, 6),
        topDiscChips: discList.slice(0, 6),
      };
    }, [cargos]);

  const filteredCargos = useMemo(() => {
    const hasLoadFilter = selectedLoadPorts.length > 0;
    const hasDiscFilter = selectedDiscPorts.length > 0;
    if (!hasLoadFilter && !hasDiscFilter)
      return cargos.filter((c) => isVisible(c?.is_status));
    return cargos.filter((c) => {
      if (!isVisible(c?.is_status)) return false;
      const l =
        PORT_ALIASES[norm(String(c.loading_port || ""))] ||
        norm(String(c.loading_port || ""));
      const d =
        PORT_ALIASES[norm(String(c.discharge_port || ""))] ||
        norm(String(c.discharge_port || ""));
      const okL = !hasLoadFilter || (l && selectedLoadPorts.includes(l));
      const okD = !hasDiscFilter || (d && selectedDiscPorts.includes(d));
      return okL && okD;
    });
  }, [cargos, selectedLoadPorts, selectedDiscPorts]);
  const filteredCargosFinal = filteredCargos;

  // ========== COUNTS: cargos on map vs total ==========
  const totalVisibleCargos = useMemo(
    () => filteredCargosFinal.length,
    [filteredCargosFinal]
  );

  const cargosWithPosition = useMemo(
    () =>
      filteredCargosFinal.filter((c) => !!getCargoLoadPos(c, portCoords))
        .length,
    [filteredCargosFinal, portCoords]
  );

  // NEW: percentage for loading badge
  const cargosWithPositionPercentage = useMemo(() => {
    if (totalVisibleCargos <= 0) return 100;
    return Math.round((cargosWithPosition / totalVisibleCargos) * 100);
  }, [cargosWithPosition, totalVisibleCargos]);

  /* ========== Cargo grouping for per-port ========== */
  const cargosGrouped = useMemo(() => {
    const byPort = new Map();
    const indexByCargoKey = new Map();
    filteredCargosFinal.forEach((c) => {
      const raw = norm(String(c.loading_port || ""));
      const loadKey = PORT_ALIASES[raw] || raw;
      if (!byPort.has(loadKey)) byPort.set(loadKey, []);
      byPort.get(loadKey).push(c);
    });
    for (const [k, arr] of byPort.entries()) {
      arr.sort((a, b) => getCargoKey(a).localeCompare(getCargoKey(b)));
      arr.forEach((c, i) => indexByCargoKey.set(getCargoKey(c), i));
    }
    return { byPort, indexByCargoKey };
  }, [filteredCargosFinal]);

  const shipCounts = useMemo(() => {
    const m = {};
    ships
      .filter((s) => isVisible(s?.is_status))
      .forEach((s) => {
        const k = norm(String(s?.open_area || ""));
        if (!k) return;
        m[k] = (m[k] || 0) + 1;
      });
    return m;
  }, [ships]);

  // ------- Mobile scroll helper -------
  function scrollMobileCardIntoView(cargoKey) {
    const el = cardRefs.current[`m-${cargoKey}`];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  useEffect(() => {
    if (window.innerWidth > 768) return;
    if (!selectedCargoKey) return;
    scrollMobileCardIntoView(selectedCargoKey);
  }, [selectedCargoKey]);

  /* ========== Map options ========== */
  const DARK_STYLE = [
    { elementType: "geometry", stylers: [{ color: "#1f2937" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#1f2937" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#e5e7eb" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "road", stylers: [{ color: "#111827" }] },
    { featureType: "water", stylers: [{ color: "#0f172a" }] },
  ];
  const mapOptions = useMemo(() => {
    const common = {
      disableDefaultUI: false,
      fullscreenControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      maxZoom: 19,
      minZoom: 2,
      gestureHandling: "greedy",
    };
    if (activeBase === "Light")
      return { ...common, mapTypeId: "roadmap", styles: [] };
    if (activeBase === "Satellite")
      return { ...common, mapTypeId: "hybrid", styles: [] };
    return { ...common, mapTypeId: "roadmap", styles: DARK_STYLE };
  }, [activeBase]);

  useEffect(() => {
    if (!mapRef.current || !route?.length) {
      fitDepsRef.current = { routeKey: null, showSidebar };
      return;
    }

    const isMobile = window.innerWidth < 768;

    const first = route[0];
    const last = route[route.length - 1];
    const routeKey = `${first[0].toFixed(3)},${first[1].toFixed(
      3
    )}->${last[0].toFixed(3)},${last[1].toFixed(3)}`;

    const prev = fitDepsRef.current;
    const routeChanged = routeKey !== prev.routeKey;
    const sidebarChanged = showSidebar !== prev.showSidebar;

    fitDepsRef.current = { routeKey, showSidebar };

    if (isMobile && sidebarChanged && !routeChanged) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    route.forEach(([lat, lng]) => bounds.extend({ lat, lng }));

    if (isMobile) {
      const bottomPadding = showSidebar ? 220 : 40;
      mapRef.current.fitBounds(bounds, {
        left: 40,
        right: 40,
        top: 80,
        bottom: bottomPadding,
      });
    } else {
      mapRef.current.fitBounds(bounds, {
        left: showSidebar ? 360 : 40,
        right: 40,
        top: 120,
        bottom: 40,
      });
    }
  }, [route, showSidebar]);

  useEffect(() => {
    if (!userPos || !mapRef.current) return;
    const didFly = sessionStorage.getItem(DID_FLY_KEY) === "1";
    const c = { lat: userPos[0], lng: userPos[1] };
    setCenter(c);
    setZoom((z) => Math.max(z, didFly ? z : 7));
    if (!didFly) sessionStorage.setItem(DID_FLY_KEY, "1");
  }, [userPos]);

  const makeRouteKey = (a, b) =>
    `${a[0].toFixed(4)},${a[1].toFixed(4)}->${b[0].toFixed(4)},${b[1].toFixed(
      4
    )}`;
  async function resolveBothPorts(loadRaw, discRaw) {
    const rawLoad = norm(String(loadRaw || ""));
    const rawDisc = norm(String(discRaw || ""));
    const loadKey = PORT_ALIASES[rawLoad] || rawLoad;
    const discKey = PORT_ALIASES[rawDisc] || rawDisc;
    let start = portCoords[loadKey];
    let end = portCoords[discKey];

    const missing = [];
    if (!start && loadKey) missing.push(loadKey);
    if (!end && discKey) missing.push(discKey);
    if (missing.length) {
      const res = await resolvePortsBatch(missing);
      if (!start && res[loadKey]) {
        start = res[loadKey];
        setPortCoords((p) => ({ ...p, [loadKey]: start }));
      }
      if (!end && res[discKey]) {
        end = res[discKey];
        setPortCoords((p) => ({ ...p, [discKey]: end }));
      }
    }
    return { start, end, loadKey, discKey };
  }
  async function fetchBestRoute(start, end) {
    const cacheKey = makeRouteKey(start, end);
    if (routeCacheRef.current.has(cacheKey))
      return routeCacheRef.current.get(cacheKey);
    const optimistic = greatCircleRoute(start, end, 160);
    try {
      const resp = await axios.post(
        `${config.base_url}/api/HappyMarineShipping/sea-route`,
        {
          start: { lat: start[0], lon: start[1] },
          end: { lat: end[0], lon: end[1] },
        },
        { timeout: 8000 }
      );
      let coords = resp?.data?.coordinates;
      if (Array.isArray(coords) && coords.length >= 2) {
        routeCacheRef.current.set(cacheKey, coords);
        return coords;
      }
    } catch {}
    routeCacheRef.current.set(cacheKey, optimistic);
    return optimistic;
  }

  const handleCargoClick = useCallback(
    async (cargo) => {
      const cargoKey = getCargoKey(cargo);

      if (selectedCargoKey === cargoKey) {
        // DESELECT cargo (and cluster)
        setSelectedCargoKey(null);
        setRoute(null);
        setDest(null);
        setSelectedClusterLoadKey(null);
        setSidebarMode("nearby");
        return;
      }

      setShowSidebar(true);
      setMobileOpen(true);
      setSelectedCargoKey(cargoKey);
      scrollCardIntoView(cargoKey);

      const { start, end } = await resolveBothPorts(
        cargo.loading_port,
        cargo.discharge_port
      );
      if (!start || !end) {
        if (start && !end) {
          const bearing = pickSeaBearing(cargo.loading_port, start[1]);
          const stub = [
            start,
            moveByBearing(start[0], start[1], 70000, bearing),
          ];
          if (selectedCargoKeyRef.current === cargoKey) {
            setRoute(stub);
            setDest(stub[stub.length - 1]);
          }
        } else {
          if (selectedCargoKeyRef.current === cargoKey) {
            setRoute(null);
            setDest(null);
          }
        }
        return;
      }
      const optimistic = greatCircleRoute(start, end, 160);
      if (selectedCargoKeyRef.current === cargoKey) {
        setRoute(optimistic);
        setDest(end);
      }
      const best = await fetchBestRoute(start, end);
      if (selectedCargoKeyRef.current === cargoKey) {
        setRoute(best);
        setDest(end);
      }
    },
    [selectedCargoKey, portCoords]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  /* =================== ZOOM THRESHOLDS =================== */
  const HIGH_ZOOM_FOR_SIDE_BY_SIDE = 9;
  const CLUSTER_UNTIL_ZOOM = 8;
  const spacingByZoom = mapZoom >= 12 ? 600 : mapZoom >= 10 ? 500 : 380;

  const handleViewList = () => {
    if (user?.is_superuser) window.location.assign("/adminLogistic");
    else if (user) window.location.assign("/userlogistics");
    else window.location.assign("/logistics");
  };

  const nearbyHeading = useMemo(() => {
    let anchorCargo = null;

    if (selectedCargoKey) {
      anchorCargo =
        cargos.find((c) => getCargoKey(c) === selectedCargoKey) || null;
    }

    if (!anchorCargo && nearbyAnchor?.key) {
      anchorCargo =
        cargos.find((c) => getCargoKey(c) === nearbyAnchor.key) || null;
    }

    const lp = anchorCargo?.loading_port?.trim();

    if (sidebarMode === "cluster") {
      // Cluster mode: show only cluster cargos — no "Nearby" word
      if (!lp) return "Cargos in Cluster";
      return `Cargos in ${lp}`;
    }

    if (sidebarMode === "nearby") {
      if (!lp) return "Nearby Cargos";
      return `Nearby Cargos in ${lp}`;
    }

    // default
    return "Nearby Cargos";
  }, [sidebarMode, selectedCargoKey, nearbyAnchor, cargos]);

  // When cluster mode is active and user closes the details
  // (selectedCargoKey becomes null), go back to "nearby" mode.
  useEffect(() => {
    if (!selectedCargoKey && selectedClusterLoadKey && sidebarMode === "cluster") {
      setSidebarMode("nearby");
    }
  }, [selectedCargoKey, selectedClusterLoadKey, sidebarMode]);

  if (!isLoaded)
    return (
      <div className="w-full h-screen flex items-center justify-center text-slate-600">
        Loading map…
      </div>
    );

  return (
    <div className="w-full h-screen overflow-hidden relative">
      <div className="">
        {navVariant === "admin" ? (
          <NewAdminNav />
        ) : navVariant === "user" ? (
          <Usernav />
        ) : (
          <NewHomeNav/>
        )}
      </div>

      {/* ===================== LEFT SIDEBAR ===================== */}
      <div
        className={`xm:hidden -mt-11 fixed top-[156px] left-0 z-[1050] transition-transform duration-300 ${
          showSidebar ? "translate-x-0" : "-translate-x-[320px]"
        }`}
        style={{ width: 320, maxWidth: "80vw", height: "100%" }}
      >
        <div className="h-full xm:hidden pb-28  backdrop-blur-md bg-white/90 dark:bg-slate-900/85 border-r border-white/40 shadow-2xl">
          <div className="flex items-center justify-between px-3 py-2 border-t border-[#123557] bg-[#9ccee2] text-[#123557]">
            <div className="font-semibold text-sm">
              {sidebarMode === "recent"
                ? `Recently Listed Cargos (${cargosWithPosition}/${totalVisibleCargos} cargos)`
                : nearbyHeading}
            </div>

            <div className="flex items-center gap-2">
              {sidebarMode !== "recent" && (
                <button
                  type="button"
                  className="px-2 py-1 rounded text-[11px] bg-white/10 hover:bg-white/20"
                  onClick={() => {
                    setSidebarMode("recent");
                    setNearbyAnchor(null);
                    setSelectedCargoKey(null);
                    setSelectedClusterLoadKey(null);
                  }}
                >
                  Recent
                </button>
              )}

              <button
                type="button"
                className="px-2 py-1 rounded bg-white/15 hover:bg-white/25"
                onClick={() => setShowSidebar((v) => !v)}
                title="Collapse"
              >
                {showSidebar ? "⟨" : "⟩"}
              </button>
            </div>
          </div>

          <div className="p-3">
            <input
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
              placeholder="Search: load, discharge, type…"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div
            ref={sidebarListRef}
            className="px-2 py-2  overflow-y-auto custom-scroll"
            style={{ height: "calc(100% - 92px)" }}
          >
            {(() => {
              let items = [];
              const anchorPos =
                sidebarMode === "nearby" ? nearbyAnchor?.pos : null;

              if (sidebarMode === "cluster" && selectedClusterLoadKey) {
                // CLUSTER MODE: show ONLY cargos inside this cluster
                const group =
                  cargosGrouped.byPort.get(selectedClusterLoadKey) || [];
                const sorted = [...group].sort((a, b) => {
                  const tsA = (() => {
                    const d =
                      a?.created_at ||
                      a?.createdAt ||
                      a?.created_on ||
                      a?.updated_at ||
                      a?.timestamp ||
                      a?.time ||
                      a?.date;
                    const n = d ? +new Date(d) : 0;
                    return Number.isFinite(n) ? n : 0;
                  })();
                  const tsB = (() => {
                    const d =
                      b?.created_at ||
                      b?.createdAt ||
                      b?.created_on ||
                      b?.updated_at ||
                      b?.timestamp ||
                      b?.time ||
                      b?.date;
                    const n = d ? +new Date(d) : 0;
                    return Number.isFinite(n) ? n : 0;
                  })();
                  return tsB - tsA;
                });

                items = sorted.map((c) => ({
                  cargo: c,
                  key: getCargoKey(c),
                  km: null,
                }));
              } else if (sidebarMode === "nearby" && anchorPos) {
                items = cargos
                  .filter((c) => isVisible(c?.is_status))
                  .map((c) => {
                    const key = getCargoKey(c);
                    const pos = getCargoLoadPos(c, portCoords);
                    if (!pos) return null;
                    const km = (() => {
                      const [lat1, lon1] = anchorPos;
                      const [lat2, lon2] = pos;
                      const R = 6371;
                      const dLat = (Math.PI / 180) * (lat2 - lat1);
                      const dLon = (Math.PI / 180) * (lon2 - lon1);
                      const p1 = Math.sin(dLat / 2) ** 2;
                      const p2 =
                        Math.cos((Math.PI / 180) * lat1) *
                        Math.cos((Math.PI / 180) * lat2) *
                        Math.sin(dLon / 2) ** 2;
                      return 2 * R * Math.asin(Math.sqrt(p1 + p2));
                    })();
                    return { cargo: c, key, km };
                  })
                  .filter(Boolean)
                  .sort((a, b) => a.km - b.km);
              } else {
                const recent = [...cargos]
                  .filter((c) => isVisible(c?.is_status))
                  .sort((a, b) => {
                    const getTs = (c) => {
                      const d =
                        c?.created_at ||
                        c?.createdAt ||
                        c?.created_on ||
                        c?.updated_at ||
                        c?.timestamp ||
                        c?.time ||
                        c?.date;
                      const n = d ? +new Date(d) : 0;
                      return Number.isFinite(n) ? n : 0;
                    };
                    return getTs(b) - getTs(a);
                  });
                items = recent.map((c) => ({
                  cargo: c,
                  key: getCargoKey(c),
                  km: null,
                }));
              }

              const q = sidebarSearch.trim().toLowerCase();
              if (q) {
                items = items.filter(({ cargo: c }) => {
                  const parts = [
                    c?.type_of_cargo,
                    c?.loading_port,
                    c?.discharge_port,
                    c?.tonnage,
                    c?.lychan,
                    c?.charterer_account,
                    c?.company,
                    c?.brief_description,
                  ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                  return parts.includes(q);
                });
              }

              if (!items.length)
                return (
                  <div className="text-xs text-slate-500 px-2">
                    No cargos found.
                  </div>
                );

              return items.map(({ cargo: c }) => {
                const cargoKey = getCargoKey(c);
                const selected = cargoKey === selectedCargoKey;
                const ownerOpen = ownerOpenKey === cargoKey;

                return (
                  <button
                    type="button"
                    key={`side-${cargoKey}`}
                    ref={(el) => (cardRefs.current[cargoKey] = el)}
                    onClick={(e) => {
                      e.preventDefault();
                      handleCargoClick(c);
                    }}
                    className={`w-full text-left mb-2 p-2 rounded-xl border transition ${
                      selected
                        ? "bg-gray-200"
                        : "bg-gray-50 shadow-lg dark:bg-slate-800 text-slate-800 border-2 border-slate-300 dark:text-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/70"
                    }`}
                  >
                    <div className="text-[13px] flex justify-between font-bold p-2 rounded-t bg-[#d1a460] text-black">
                      <p>
                        CARGO :{" "}
                        {(c?.type_of_cargo)?.toUpperCase() || "Cargo"}
                      </p>
                    </div>

                    <ul className="list-disc p-2 list-inside space-y-1">
                      <li
                        className={`text-[11px] ${
                          selected ? "opacity-95" : "opacity-80"
                        }`}
                      >
                        <span className="font-semibold">
                          Loading Port
                        </span>{" "}
                        : {c?.loading_port || "-"}
                      </li>
                      <li
                        className={`text-[11px] ${
                          selected ? "opacity-95" : "opacity-80"
                        }`}
                      >
                        <span className="font-semibold">
                          Discharge Port
                        </span>{" "}
                        : {c?.discharge_port || "-"}
                      </li>
                      <li
                        className={`text-[11px] ${
                          selected ? "opacity-95" : "opacity-80"
                        }`}
                      >
                        <span className="font-semibold">Tonnage</span> :{" "}
                        {c?.tonnage || "-"}
                      </li>

                      <li
                        className={`list-none ${
                          selected ? "opacity-95 hidden" : "opacity-80"
                        }`}
                      >
                        <span className={`text-[15px] font-bold float-end`}>
                          REF ID : {c?.id}
                        </span>
                      </li>

                      <div
                        className="overflow-y-auto transition-[max-height] duration-300 ease-out"
                        style={{ maxHeight: selected ? 280 : 0 }}
                      >
                        <div className="pb-2">
                          <div className="text-[11px]">
                            <li>
                              <span className="font-semibold">
                                Laycan
                              </span>
                              : {c?.lychan || "-"}
                            </li>
                            <li>
                              <span className="font-semibold">
                                Commission
                              </span>
                              : {c?.commission || "-"}
                            </li>
                            <li className="col-span-2">
                              <span className="font-semibold">
                                Charterer
                              </span>
                              : {c?.charterer_account || "-"}
                            </li>
                            {c?.brief_description && (
                              <div className="col-span-2">
                                <h1 className="font-semibold text-sm   rounded mt-2">
                                  Additional Details
                                </h1>
                                {c?.brief_description
                                  ?.split("\n")
                                  .map((line, idx) => (
                                    <p
                                      key={idx}
                                      className="text-sm text-gray-800 leading-relaxed"
                                    >
                                      {line}
                                    </p>
                                  ))}
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className=" w-full bg-[#25D366] text-sm hover:opacity-90 text-white font-semibold py-2 px-3 rounded"
                            onClick={(e) => {
                              e.preventDefault();
                              const url = new URL(
                                window.location.origin +
                                  window.location.pathname
                              );
                              url.searchParams.set(
                                "cargo",
                                getCargoKey(c)
                              );
                              const wa = `https://wa.me/?text=${encodeURIComponent(
                                `${
                                  (c?.type_of_cargo || "Cargo") +
                                  " Available"
                                }\nThis cargo from ${
                                  c?.loading_port || "-"
                                } to ${
                                  c?.discharge_port || "-"
                                }.\n${url.toString()}`
                              )}`;
                              window.open(
                                wa,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                          >
                            Share via WhatsApp
                          </button>

                          <div className="mt-2 flex items-center justify-between text-[9px] opacity-80">
                            <div className="w-full">
                              <button
                                type="button"
                                className={`px-2 py-[6px] rounded text-white flex items-center gap-1
                                  ${
                                    isLoggedIn
                                      ? "bg-blue-600 hover:bg-blue-700"
                                      : "bg-slate-400 cursor-pointer"
                                  }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!isLoggedIn) {
                                    toast.warn("Login required");
                                    return;
                                  }
                                  setOwnerOpenKey((k) =>
                                    k === cargoKey ? null : cargoKey
                                  );
                                }}
                                aria-expanded={
                                  isLoggedIn ? ownerOpen : false
                                }
                                aria-disabled={!isLoggedIn}
                                title={
                                  isLoggedIn
                                    ? "Show owner details"
                                    : "Login required"
                                }
                              >
                                Owner Information
                                <svg
                                  className={`h-3 w-3 transition-transform ${
                                    ownerOpen && isLoggedIn
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>

                              <div
                                className="overflow-hidden transition-[max-height] duration-300 ease-out"
                                style={{
                                  maxHeight:
                                    isLoggedIn && ownerOpen ? 160 : 0,
                                }}
                              >
                                <div className="mt-2 rounded-lg bg-white/70 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 p-2 text-[11px]">
                                  <ul className="space-y-1">
                                    <li>
                                      <span className="font-semibold">
                                        Name:
                                      </span>{" "}
                                      {c?.name || "-"}
                                    </li>
                                    <li>
                                      <span className="font-semibold">
                                        Email:
                                      </span>{" "}
                                      {c?.email ? (
                                        <a
                                          href={`mailto:${c.email}`}
                                          className="underline hover:no-underline"
                                          onClick={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          {c.email}
                                        </a>
                                      ) : (
                                        "-"
                                      )}
                                    </li>
                                    <li>
                                      <span className="font-semibold">
                                        Phone:
                                      </span>{" "}
                                      {c?.phone ? (
                                        <a
                                          href={`tel:${c.phone}`}
                                          className="underline hover:no-underline"
                                          onClick={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          {c.phone}
                                        </a>
                                      ) : (
                                        "-"
                                      )}
                                    </li>
                                    {c?.company && (
                                      <li>
                                        <span className="font-semibold">
                                          Company:
                                        </span>{" "}
                                        {c.company}
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {user?.is_superuser && (
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className="p-1 rounded hover:bg-white/30"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditClick(c);
                                  }}
                                  title="Edit"
                                >
                                  <i className="fa-solid text-blue-500 fa-pen"></i>
                                </button>
                                <button
                                  type="button"
                                  className="p-1 rounded hover:bg-white/30"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteClick(c);
                                  }}
                                  title="Delete"
                                >
                                  <i className="fa-solid text-red-500 fa-trash"></i>
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="mt-1 text-[9px] opacity-80">
                            {timeAgo(
                              c?.created_at ||
                                c?.createdAt ||
                                c?.updated_at
                            )}
                            <span
                              className={`text-[15px] font-bold float-end `}
                            >
                              REF ID : {c?.id}
                            </span>
                          </div>
                        </div>
                      </div>
                    </ul>
                  </button>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* ============ MOBILE BOTTOM SIDEBAR (xm screens) ============ */}
      <div
        className={`hidden xm:block fixed bottom-0 left-0 right-0 z-[1050] 
    transition-transform duration-300 bg-white/95 dark:bg-slate-900/90 shadow-2xl 
    ${mobileOpen ? "translate-y-0" : "translate-y-[100%]"}`}
        style={{
          height: "38%",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
        }}
      >
        <div
        onClick={() => setMobileOpen(false)}
          className="relative flex items-center justify-between px-3 py-2 bg-[#253356] text-white rounded-t-2xl"
        >
          <div  className="absolute left-1/2 -translate-x-1/2 -top-2 w-12 h-1.5 bg-white/70 rounded-full" />
          <div  className="font-semibold text-sm">
            {mobileOpen ? "Cargos" : "Open Cargo List"}
          </div>
          
            <div
              
              className="px-2 py-1 rounded bg-white/15 hover:bg-white/25 text-lg leading-none"
             
            >
              ▾
            </div>
          
        </div>

        <div className="px-4 py-2 flex items-center justify-between border-b border-slate-200 dark:border-slate-700">
          <div className="font-semibold text-sm">
            {sidebarMode === "recent"
              ? `Recently Listed Cargos (${cargosWithPosition}/${totalVisibleCargos} cargos)`
              : nearbyHeading}
          </div>
        </div>

        <div
          className="overflow-y-auto px-3 pb-20 custom-scroll"
          style={{ height: "calc(100% - 120px)" }}
        >
          {filteredCargosFinal.map((c) => {
            const cargoKey = getCargoKey(c);
            const selected = selectedCargoKey === cargoKey;
            const ownerOpen = ownerOpenKey === cargoKey;

            return (
              <button
                type="button"
                key={`mobile-${cargoKey}`}
                ref={(el) => (cardRefs.current[`m-${cargoKey}`] = el)}
                onClick={(e) => {
                  e.preventDefault();
                  handleCargoClick(c);
                }}
                className={`w-full text-left mb-3 p-3 rounded-xl border ${
                  selected
                    ? "bg-gray-200 border-gray-400"
                    : "bg-gray-50 border-slate-300 dark:bg-slate-800 dark:border-slate-700"
                }`}
              >
                <div className="text-[13px] flex justify-between font-bold p-2 rounded-t bg-[#d1a460] text-black">
                  <p>CARGO : {c?.type_of_cargo?.toUpperCase()}</p>
                </div>

                <ul className="list-disc p-2 list-inside space-y-1 text-[12px]">
                  <li>
                    <b>Loading Port:</b> {c.loading_port}
                  </li>
                  <li>
                    <b>Discharge Port:</b> {c.discharge_port}
                  </li>
                  <li>
                    <b>Tonnage:</b> {c.tonnage}
                  </li>
                  <li className="list-none text-right font-bold">
                    REF ID: {c.id}
                  </li>

                  <div
                    className="overflow-y-auto transition-[max-height] duration-300 ease-out"
                    style={{ maxHeight: selected ? 280 : 0 }}
                  >
                    <div className="pb-2">
                      <div className="text-[11px]">
                        <li>
                          <span className="font-semibold">Laycan</span>:{" "}
                          {c?.lychan || "-"}
                        </li>
                        <li>
                          <span className="font-semibold">
                            Commission
                          </span>
                          : {c?.commission || "-"}
                        </li>
                        <li className="col-span-2">
                          <span className="font-semibold">
                            Charterer
                          </span>
                          : {c?.charterer_account || "-"}
                        </li>
                        <div className="col-span-2">
                          {c?.brief_description
                            ?.split("\n")
                            .map((line, idx) => (
                              <li
                                key={idx}
                                className="text-sm text-gray-800 leading-relaxed"
                              >
                                {line}
                              </li>
                            ))}
                        </div>
                      </div>

                      <button
                        type="button"
                        className=" w-full bg-[#25D366] text-sm hover:opacity-90 text-white font-semibold py-2 px-3 rounded"
                        onClick={(e) => {
                          e.preventDefault();
                          const url = new URL(
                            window.location.origin +
                              window.location.pathname
                          );
                          url.searchParams.set("cargo", getCargoKey(c));
                          const wa = `https://wa.me/?text=${encodeURIComponent(
                            `${
                              (c?.type_of_cargo || "Cargo") + " Available"
                            }\nCheck cargo from ${
                              c?.loading_port || "-"
                            } to ${
                              c?.discharge_port || "-"
                            }.\n${url.toString()}`
                          )}`;
                          window.open(
                            wa,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                      >
                        Share via WhatsApp
                      </button>

                      <div className="mt-2 flex items-center justify-between text-[9px] opacity-80">
                        <div className="w-full">
                          <button
                            type="button"
                            className={`px-2 py-[6px] rounded text-white flex items-center gap-1
                                  ${
                                    isLoggedIn
                                      ? "bg-blue-600 hover:bg-blue-700"
                                      : "bg-slate-400 cursor-pointer"
                                  }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!isLoggedIn) {
                                toast.warn("Login required");
                                return;
                              }
                              setOwnerOpenKey((k) =>
                                k === cargoKey ? null : cargoKey
                              );
                            }}
                            aria-expanded={isLoggedIn ? ownerOpen : false}
                            aria-disabled={!isLoggedIn}
                            title={
                              isLoggedIn
                                ? "Show owner details"
                                : "Login required"
                            }
                          >
                            Owner Information
                            <svg
                              className={`h-3 w-3 transition-transform ${
                                ownerOpen && isLoggedIn
                                  ? "rotate-180"
                                  : ""
                              }`}
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>

                          <div
                            className="overflow-hidden transition-[max-height] duration-300 ease-out"
                            style={{
                              maxHeight:
                                isLoggedIn && ownerOpen ? 160 : 0,
                            }}
                          >
                            <div className="mt-2 rounded-lg bg-white/70 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 p-2 text-[11px]">
                              <ul className="space-y-1">
                                <li>
                                  <span className="font-semibold">
                                    Name:
                                  </span>{" "}
                                  {c?.name || "-"}
                                </li>
                                <li>
                                  <span className="font-semibold">
                                    Email:
                                  </span>{" "}
                                  {c?.email ? (
                                    <a
                                      href={`mailto:${c.email}`}
                                      className="underline hover:no-underline"
                                      onClick={(e) =>
                                        e.stopPropagation()
                                      }
                                    >
                                      {c.email}
                                    </a>
                                  ) : (
                                    "-"
                                  )}
                                </li>
                                <li>
                                  <span className="font-semibold">
                                    Phone:
                                  </span>{" "}
                                  {c?.phone ? (
                                    <a
                                      href={`tel:${c.phone}`}
                                      className="underline hover:no-underline"
                                      onClick={(e) =>
                                        e.stopPropagation()
                                      }
                                    >
                                      {c.phone}
                                    </a>
                                  ) : (
                                    "-"
                                  )}
                                </li>
                                {c?.company && (
                                  <li>
                                    <span className="font-semibold">
                                      Company:
                                    </span>{" "}
                                    {c.company}
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>

                        {user?.is_superuser && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-white/30"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEditClick(c);
                              }}
                              title="Edit"
                            >
                              <i className="fa-solid text-blue-500 fa-pen"></i>
                            </button>
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-white/30"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteClick(c);
                              }}
                              title="Delete"
                            >
                              <i className="fa-solid text-red-500 fa-trash"></i>
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="mt-1 text-[9px] opacity-80">
                        {timeAgo(
                          c?.created_at ||
                            c?.createdAt ||
                            c?.updated_at
                        )}
                        <span
                          className={`text-[15px] font-bold float-end `}
                        >
                          REF ID : {c?.id}
                        </span>
                      </div>
                    </div>
                  </div>
                </ul>
              </button>
            );
          })}
        </div>
        <div className="px-4 py-2">
          <input
            value={sidebarSearch}
            onChange={(e) => setSidebarSearch(e.target.value)}
            placeholder="Search: load, discharge, type..."
          />
        </div>
      </div>

      {/* EDIT MODAL */}
      {editCargo && (
        <div className="fixed inset-0 z-[1200] bg-black/40 flex items-center justify-center">
          <div className="w-[680px] max-w-[90vw] rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div className="font-semibold">Edit Cargo</div>
              <div className="flex gap-2">
                {editForm.url_new && (
                  <button
                    type="button"
                    onClick={() => window.open(editForm.url_new, "_blank")}
                    className="bg-blue-600 mt-1 transition-all duration-300 hover:bg-blue-500 rounded px-2 py-1 text-white"
                  >
                    Ref Email
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleRepost}
                  className="bg-green-600 mt-1 transition-all duration-300 hover:bg-green-500 rounded px-2 py-1 text-white"
                >
                  Repost
                </button>
                <button
                  type="button"
                  className="px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => setEditCargo(null)}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4 grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-3">
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Type of Cargo
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.type_of_cargo}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        type_of_cargo: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Loading Port
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.loading_port}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        loading_port: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Discharge Port
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.discharge_port}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        discharge_port: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Tonnage
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.tonnage}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        tonnage: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Laycan
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.lychan}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        lychan: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Commission
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.commission}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        commission: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <div className="space-y-3">
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Charterer Account
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.charterer_account}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        charterer_account: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Owner Name
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        name: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Owner Email
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        email: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Owner Phone
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        phone: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    Company
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.company}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        company: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="block">
                  <div className="text-xs text-slate-500 mb-1">
                    URL
                  </div>
                  <input
                    className="w-full border rounded px-2 py-1"
                    value={editForm.url_new}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        url_new: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <label className="block col-span-2">
                <div className="text-xs text-slate-500 mb-1">
                  Brief Description
                </div>
                <textarea
                  className="w-full border rounded px-2 py-1 h-24"
                  value={editForm.brief_description}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      brief_description: e.target.value,
                    }))
                  }
                />
              </label>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded bg-slate-200 dark:bg-slate-700"
                onClick={() => setEditCargo(null)}
                disabled={isSavingEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded bg-indigo-600 text-white disabled:opacity-60"
                onClick={saveEdit}
                disabled={isSavingEdit}
              >
                {isSavingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {confirmDel && (
        <div className="fixed inset-0 z-[1200] bg-black/40 flex items-center justify-center">
          <div className="w-[420px] max-w-[90vw] rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="text-base font-semibold mb-2">
              Delete cargo?
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded bg-slate-200 dark:bg-slate-700"
                onClick={() => setConfirmDel(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded bg-red-600 text-white disabled:opacity-60"
                onClick={confirmDeleteNow}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!showSidebar && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setShowSidebar(true);
          }}
          className="fixed  xm:hidden left-2 top-[180px] z-[1051] bg-white/90 dark:bg-slate-800/85 border border-white/40 shadow-xl rounded-lg px-3 py-2 text-sm"
          title="Open cargo list"
        >
          ☰ Cargo Details
        </button>
      )}

      {!mobileOpen && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setMobileOpen(true);
          }}
          className="fixed  hidden xm:block left-2 bottom-2 z-[1051] bg-white/90 dark:bg-slate-800/85 border border-white/40 shadow-xl rounded-lg px-3 py-2 text-sm"
          title="Open cargo list"
        >
          ^ Cargo Details
        </button>
      )}

      <div className="absolute xm:bottom-2 xm:right-2 right-6 bottom-6 z-[1000] pointer-events-none">
        <button
          type="button"
          onClick={() => setShowShips((v) => !v)}
          className="pointer-events-auto bg-[#253356] hover:bg-[#1b2a4f] text-white font-semibold px-4 py-3 rounded-xl shadow-lg"
        >
          {showShips ? "Hide Ships" : "View Ships Available"}
        </button>
      </div>

      <div className="absolute left-[340px] xm:left-2 top-44 z-[1000] pointer-events-none">
        <div className="flex gap-3 items-center pointer-events-auto ">
          <button
            type="button"
            onClick={handleViewList}
            className="bg-[#d1a460] px-3 py-2 rounded-xl text-gray-50 shadow-md hover:shadow-lg transition"
          >
            View List
          </button>
        </div>
      </div>

      {/* Base map selector + CARGOS COUNT BADGE */}
      <div className="absolute xm:float-right xm:flex-col xm:gap-2 flex items-center gap-5 right-14 top-44 z-[1000]">
        {/* CARGOS COUNT BADGE with spinner / tick */}
        {cargosWithPositionPercentage < 100 && (
          <div className="z-[1100] bg-[#123d5f] py-2 text-white dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 shadow-lg rounded-full px-3 text-[12px] font-medium dark:text-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-5 h-4 flex items-center justify-center rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900">
                {cargosWithPositionPercentage < 100 ? (
                  <div className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                ) : (
                  <span className="text-xs text-green-600">✓</span>
                )}
              </div>
              <span className="text-sm">
                {cargosWithPositionPercentage < 100
                  ? `Loading cargos ${cargosWithPositionPercentage}%`
                  : "All cargos Loaded"}
              </span>
            </div>
          </div>
        )}

        <label htmlFor="basemap-select" className="sr-only">
          Base map
        </label>
        <select
          id="basemap-select"
          value={activeBase}
          onChange={(e) => setActiveBase(e.target.value)}
          className="w-24 xm:float-end py-2 text-sm rounded-md bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-100 border border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="Light">Light</option>
          <option value="Dark">Dark</option>
          <option value="Satellite">Satellite</option>
        </select>
      </div>

      {/* NEW CARGO APPROVED NOTIFICATION (bottom-right) */}
      {newCargoNotification && (
        <div className="fixed right-4 bottom-4 z-[1300]">
          <div
            className="cursor-pointer max-w-xs bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 shadow-2xl rounded-xl p-3"
            onClick={() => {
              const c = newCargoNotification.cargo;
              setShowSidebar(true);
              handleCargoClick(c);
              setNewCargoNotification(null);
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-xs font-semibold text-slate-900 dark:text-slate-50">
                  New cargo approved
                </div>
                <div className="mt-1 text-[11px] text-slate-700 dark:text-slate-200">
                  {(newCargoNotification.cargo?.type_of_cargo || "Cargo") +
                    " — REF " +
                    (newCargoNotification.cargo?.id ?? "")}
                  <br />
                  {(newCargoNotification.cargo?.loading_port || "-") +
                    " → " +
                    (newCargoNotification.cargo?.discharge_port || "-")}
                </div>
                <div className="mt-2 text-[11px] text-indigo-600 dark:text-indigo-300">
                  Click to open in sidebar
                </div>
              </div>
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600 text-xs mt-[-4px]"
                onClick={(e) => {
                  e.stopPropagation();
                  setNewCargoNotification(null);
                }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAP */}
      
        <GoogleMap
          onLoad={(map) => {
            mapRef.current = map;
            setGmapsReady(true);
            const up = readLastUserPos();
            const c = up
              ? { lat: up[0], lng: up[1] }
              : {
                  lat: INITIAL_CENTER[0],
                  lng: INITIAL_CENTER[1],
                };
            setCenter(c);
            setZoom((z) => Math.max(z, 5));
          }}
          onUnmount={() => {
            mapRef.current = null;
            setGmapsReady(false);
          }}
          mapContainerStyle={{ width: "100%", height: "100vh" }}
          center={center}
          zoom={zoom}
          options={mapOptions}
          onZoomChanged={() =>
            setMapZoom(mapRef.current?.getZoom() || 5)
          }
          onClick={() => {
            setSelectedCargoKey(null);
            setSelectedShipKey(null);
            setRoute(null);
            setDest(null);
            if (selectedClusterLoadKey) {
              setSelectedClusterLoadKey(null);
              if (sidebarMode === "cluster") {
                setSidebarMode("nearby");
              }
            }
          }}
        >
          {ALWAYS_VISIBLE_PORTS.map((p) => (
            <MarkerF
              key={`port-${p.name}`}
              position={{ lat: p.lat, lng: p.lon }}
              options={{ clickable: false, opacity: 0 }}
              title={p.name}
            />
          ))}

          {/* ================== CARGOS: CLUSTER WHEN ZOOMED OUT ================== */}
          {(() => {
            const markers = [];
            const hideOthers = !!selectedCargoKey && sidebarMode !== "cluster";

            if (hideOthers) {
              const cargo = filteredCargosFinal.find(
                (c) => getCargoKey(c) === selectedCargoKey
              );
              if (!cargo) return null;
              const rawLoad = norm(String(cargo.loading_port || ""));
              const loadKey = PORT_ALIASES[rawLoad] || rawLoad;
              const start =
                portCoords[loadKey] || fallbackGuessCoords(loadKey);
              if (!start) return null;

              const pos = inlandAnchor(
                start[0],
                start[1],
                cargo.loading_port,
                0.35
              );

              return (
                <React.Fragment key={`cargo-only-${getCargoKey(cargo)}`}>
                  <MarkerF
                    position={{ lat: pos[0], lng: pos[1] }}
                    icon={haloIcon({ diameter: 84 })}
                    clickable={false}
                    zIndex={895}
                    options={{ optimized: false }}
                  />
                  <MarkerF
                    position={{ lat: pos[0], lng: pos[1] }}
                    icon={makeRefBadgeIcon(`REF ${getId(cargo)}`, {
                      yOffset: 42,
                    })}
                    clickable={false}
                    zIndex={1190}
                    options={{ optimized: false }}
                  />
                  <MarkerF
                    position={{ lat: pos[0], lng: pos[1] }}
                    onClick={(ev) => {
                      handleCargoClick(cargo);
                      ev.domEvent?.stopPropagation();
                    }}
                    onMouseOver={() =>
                      setHoveredCargoKey(getCargoKey(cargo))
                    }
                    onMouseOut={() =>
                      setHoveredCargoKey((k) =>
                        k === getCargoKey(cargo) ? null : k
                      )
                    }
                    icon={selectedCargoIcon}
                    options={{ optimized: false }}
                    zIndex={1200}
                    title={`${pretty(loadKey)} — ${
                      cargo.type_of_cargo || "Cargo"
                    }`}
                  />
                </React.Fragment>
              );
            }

            for (const [loadKey, group] of cargosGrouped.byPort.entries()) {
              const start =
                portCoords[loadKey] || fallbackGuessCoords(loadKey);
              if (!start) continue;

              const inlandKm = 0.35;

              if (mapZoom <= CLUSTER_UNTIL_ZOOM && group.length > 1) {
                const clusterPos = inlandAnchor(
                  start[0],
                  start[1],
                  group[0]?.loading_port || loadKey,
                  inlandKm
                );
                const count = group.length;
                const isSelectedCluster =
                  selectedClusterLoadKey === loadKey;

                if (isSelectedCluster) {
                  markers.push(
                    <MarkerF
                      key={`cluster-halo-${loadKey}`}
                      position={{
                        lat: clusterPos[0],
                        lng: clusterPos[1],
                      }}
                      icon={haloIcon({ diameter: 84 })}
                      clickable={false}
                      zIndex={945}
                      options={{ optimized: false }}
                    />
                  );
                }

                markers.push(
                  <React.Fragment key={`cluster-${loadKey}`}>
                    <MarkerF
                      position={{
                        lat: clusterPos[0],
                        lng: clusterPos[1],
                      }}
                      onClick={(ev) => {
                        ev.domEvent?.stopPropagation();

                        // Mark this cluster as selected
                        setSelectedClusterLoadKey(loadKey);

                        // Prepare "nearby" anchor for later
                        setNearbyAnchor({
                          key: loadKey,
                          pos: clusterPos,
                        });

                        // Switch sidebar into "cluster" mode
                        setSidebarMode("cluster");
                        setShowSidebar(true);

                        // Pick the LATEST cargo in this cluster
                        const getTs = (c) => {
                          const d =
                            c?.created_at ||
                            c?.createdAt ||
                            c?.created_on ||
                            c?.updated_at ||
                            c?.timestamp ||
                            c?.time ||
                            c?.date;
                          const n = d ? +new Date(d) : 0;
                          return Number.isFinite(n) ? n : 0;
                        };
                        let latestCargo = group[0];
                        for (const c of group) {
                          if (getTs(c) > getTs(latestCargo)) {
                            latestCargo = c;
                          }
                        }

                        // Show latest cargo route + details
                        handleCargoClick(latestCargo);
                      }}
                      icon={cargoIcon}
                      options={{ optimized: false }}
                      zIndex={950}
                      title={`${pretty(loadKey)} — ${count} cargos`}
                    />
                    <MarkerF
                      position={{
                        lat: clusterPos[0],
                        lng: clusterPos[1],
                      }}
                      icon={makeRefBadgeIcon(String(count), {
                        yOffset: 42,
                        bg: isSelectedCluster ? "#22C55E" : "#EF4444",
                      })}
                      clickable={false}
                      zIndex={960}
                      options={{ optimized: false }}
                    />
                  </React.Fragment>
                );
              } else {
                const spacingMeters =
                  mapZoom >= 12 ? 140 : mapZoom >= 10 ? 110 : 90;
                const positions =
                  group.length > 1
                    ? inlandSideBySidePositions(
                        start[0],
                        start[1],
                        group[0]?.loading_port || loadKey,
                        group.length,
                        spacingMeters,
                        inlandKm
                      )
                    : [
                        inlandAnchor(
                          start[0],
                          start[1],
                          group[0]?.loading_port || loadKey,
                          inlandKm
                        ),
                      ];

                group.forEach((cargo, i) => {
                  const key = getCargoKey(cargo);
                  const pos =
                    positions[Math.min(i, positions.length - 1)];
                  const isSelected = key === selectedCargoKey;
                  const showBadge =
                    isSelected || hoveredCargoKey === key;

                  markers.push(
                    <React.Fragment key={`cargo-${key}`}>
                      {isSelected && (
                        <MarkerF
                          position={{ lat: pos[0], lng: pos[1] }}
                          icon={haloIcon({ diameter: 84 })}
                          clickable={false}
                          zIndex={895}
                          options={{ optimized: false }}
                        />
                      )}
                      {showBadge && (
                        <MarkerF
                          position={{ lat: pos[0], lng: pos[1] }}
                          icon={makeRefBadgeIcon(`REF ${getId(cargo)}`, {
                            yOffset: 42,
                          })}
                          clickable={false}
                          zIndex={isSelected ? 1190 : 880}
                          options={{ optimized: false }}
                        />
                      )}
                      <MarkerF
                        position={{ lat: pos[0], lng: pos[1] }}
                        onClick={(ev) => {
                          handleCargoClick(cargo);
                          ev.domEvent?.stopPropagation();
                        }}
                        onMouseOver={() => setHoveredCargoKey(key)}
                        onMouseOut={() =>
                          setHoveredCargoKey((k) =>
                            k === key ? null : k
                          )
                        }
                        icon={isSelected ? selectedCargoIcon : cargoIcon}
                        options={{ optimized: false }}
                        zIndex={isSelected ? 1200 : 900}
                        title={`${pretty(loadKey)} — ${
                          cargo.type_of_cargo || "Cargo"
                        }`}
                      />
                    </React.Fragment>
                  );
                });
              }
            }

            return markers;
          })()}

          {/* SHIPS (now forced offshore using SHIP_AREA_OVERRIDES + larger distance) */}
          {showShips &&
            ships
              .filter((s) => isVisible(s?.is_status))
              .map((ship, i) => {
                const areaKey = normAreaKey(ship.open_area || "");
                const baseOverride = SHIP_AREA_OVERRIDES[areaKey];
                let base =
                  baseOverride ||
                  areaCoords[areaKey] ||
                  applyAreaOverrideIfAny(areaKey, undefined);
                if (!base) return null;

                const groupSize = shipCounts[areaKey] || 1;
                let finalPos;

                if (
                  (mapRef.current?.getZoom() || 5) >=
                    HIGH_ZOOM_FOR_SIDE_BY_SIDE &&
                  groupSize > 1
                ) {
                  const bearing = pickSeaBearing(
                    ship.open_area,
                    base[1]
                  );
                  const distKm = pickSeaDistanceKm(
                    ship.open_area,
                    60
                  );
                  const anchor = moveByBearing(
                    base[0],
                    base[1],
                    distKm * 1000,
                    bearing
                  );
                  const lateralBearing = (bearing + 90) % 360;
                  const halfSpan = (groupSize - 1) / 2;
                  const spacingMeters = spacingByZoom;
                  const delta = (i - halfSpan) * spacingMeters;
                  const dir =
                    delta >= 0
                      ? lateralBearing
                      : (lateralBearing + 180) % 360;
                  finalPos = moveByBearing(
                    anchor[0],
                    anchor[1],
                    Math.abs(delta),
                    dir
                  );
                } else {
                  const baseBearing = pickSeaBearing(
                    ship.open_area,
                    base[1]
                  );
                  const startKm = pickSeaDistanceKm(
                    ship.open_area,
                    60
                  );
                  const perRing = 6;
                  const ringIdx = Math.floor(i / perRing);
                  const jitter = ((i * 41) % 31) - 15;
                  const bearing = baseBearing + jitter;
                  const km = startKm + ringIdx * 8;
                  finalPos = moveByBearing(
                    base[0],
                    base[1],
                    km * 1000,
                    bearing
                  );
                }

                const shipKeyFull = getShipKey(ship);
                const isSelected = shipKeyFull === selectedShipKey;
                const showBadge =
                  isSelected || hoveredShipKey === shipKeyFull;

                return (
                  <React.Fragment key={`ship-${shipKeyFull}`}>
                    {showBadge && (
                      <MarkerF
                        position={{
                          lat: finalPos[0],
                          lng: finalPos[1],
                        }}
                        icon={makeRefBadgeIcon(`REF ${getId(ship)}`, {
                          yOffset: 42,
                        })}
                        clickable={false}
                        zIndex={isSelected ? 1090 : 790}
                        options={{ optimized: false }}
                      />
                    )}
                    <MarkerF
                      position={{
                        lat: finalPos[0],
                        lng: finalPos[1],
                      }}
                      onClick={(ev) => {
                        setSelectedShipKey((k) =>
                          k === shipKeyFull ? null : k
                        );
                        ev.domEvent?.stopPropagation();
                      }}
                      onMouseOver={() =>
                        setHoveredShipKey(shipKeyFull)
                      }
                      onMouseOut={() =>
                        setHoveredShipKey((k) =>
                          k === shipKeyFull ? null : k
                        )
                      }
                      icon={shipIcon}
                      options={{ optimized: false }}
                      zIndex={isSelected ? 1100 : 800}
                    />
                  </React.Fragment>
                );
              })}

          {/* ROUTE */}
          {Array.isArray(route) && route.length >= 2 && (
            <PolylineF
              path={route.map(([lat, lng]) => ({ lat, lng }))}
              options={{
                strokeColor:
                  activeBase === "Dark"
                    ? "#CFFF04"
                    : activeBase === "Satellite"
                    ? "#F59E0B"
                    : "#2563EB",
                strokeOpacity: 1,
                strokeWeight: 3,
                icons: [
                  {
                    icon: {
                      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                      scale: 3,
                    },
                    offset: "98%",
                  },
                ],
              }}
            />
          )}

          {/* DESTINATION pin */}
          {dest && (
            <MarkerF
              position={{ lat: dest[0], lng: dest[1] }}
              icon={destIcon}
              options={{ optimized: false }}
              zIndex={2000}
            />
          )}
        </GoogleMap>


      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scroll::-webkit-scrollbar-thumb { background: rgba(100,116,139,.35); border-radius: 999px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>
  );
}


